import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { PhotoViewer } from '@awesome-cordova-plugins/photo-viewer/ngx';
import { LOYALTY_BADGES } from 'src/app/shared/components/loyalty-badge/constants/loyalty-badges';
import { LoyaltyBadgeVariant } from 'src/app/shared/components/loyalty-badge/types/loyalty-badge.type';
/**
 * Renders the user avatar or just the initials if no picture available.
 * Future: https://angular.io/guide/dynamic-component-loader
 */
@Component({
  selector: 'user-avatar-gql',
  templateUrl: './user-avatar-gql.component.html',
  styleUrls: ['./user-avatar-gql.component.scss'],
})
export class UserAvatarGqlComponent implements OnInit, OnChanges {
  // name initial
  public initials: string;

  // avatar size
  @Input() size = 48;

  // font
  font;

  // show large image flag
  @Input() showLargeImage = false;

  // user name
  @Input() name: string;

  // user avatar object
  @Input() avatar: { big?: string; small?: string; medium?: string } | string = {};

  // user avatar size 'small' | 'big' | 'medium'
  @Input() avatarSize: 'small' | 'big' | 'medium' = 'small';

  // if true, shows a green online indicator on avatar
  @Input() isOnline = false;

  // alt text for avatar image
  @Input() avatarAltText = '';

  /**
   * The specific loyalty badge level of the worker, to be displayed on the avatar.
   */
  @Input() loyaltyBadgeLevel: keyof typeof LOYALTY_BADGES | null = null;

  /**
   * Which variant of the badge should display.
   */
  @Input() loyaltyBadgeVariant: LoyaltyBadgeVariant = 'full';

  // check image is default or not
  isDefaultImage = false;

  constructor(private photoViewer: PhotoViewer) {}

  ngOnInit() {
    this.font = (8 + this.size * 0.2).toFixed();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.name || changes.avatar) {
      this.initializeUser();
    }
  }

  /**
   * check user object is anonymous
   *
   */
  userIsAnonymous(): boolean {
    // anonymous users are returned with a name "a mable member"
    return this.name?.toLowerCase() === 'a mable member';
  }

  /**
   * Initialize user avatar
   */
  initializeUser() {
    // There are some odd cases where a string would be provided in the avatar object and a function called `small()` could be loaded instead (a bug).
    // So an additional check was added to confirm its not a string. If its not, do the default check to get the url
    // of a correct size. Fallback to an empty string if all fails.
    const avatarLink = typeof this.avatar !== 'string' ? this.avatar?.[this.avatarSize] || '' : this.avatar;

    // If users avatar is default use initials
    if (avatarLink === '' || avatarLink?.match(/default_avatars/)) {
      this.initials = this.initialsFor(this.name);
      this.isDefaultImage = true;
    } else {
      this.avatar = avatarLink;
    }
  }

  /**
   * Get initials from name
   *
   * @param name - name for get initials
   */
  private initialsFor(name: string): string {
    // get the initials
    const initials =
      name
        ?.toUpperCase()
        .split(' ')
        .map((w) => w[0]) || [];
    const numToRemove = Math.max(0, initials.length - 2);

    initials.splice(1, numToRemove);

    return initials.join('');
  }

  /**
   * show image as large view
   *
   */
  enlargeImage() {
    if (this.showLargeImage && typeof this.avatar !== 'string') {
      const options = {
        share: false,
        closeButton: true,
        copyToReference: true,
        headers: '',
        piccasoOptions: {},
      };
      this.photoViewer.show(this.avatar?.big, this.name, options);
    }
  }
}
